import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as BarTooltip,
} from 'recharts';

import styles, { rechartsStyles } from './HomeBarChart.styles';
import { buildHomeBarData } from '../../utils/home.helpers';

const CHART_COLORS = ['#5236AB', '#EA6361'];
const LEGEND_ENTRIES = [
  { label: 'Success', color: CHART_COLORS[0] },
  { label: 'Failure', color: CHART_COLORS[1] },
];

const InlineLegend = React.memo(function InlineLegend() {
  return (
    <Box sx={styles.inlineLegend}>
      {LEGEND_ENTRIES.map((e) => (
        <Box key={e.label} sx={styles.inlineLegendItem}>
          <Box sx={{ ...styles.inlineLegendSwatch, backgroundColor: e.color }} />
          <Typography variant="caption" sx={styles.inlineLegendText}>
            {e.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
});

export default function HomeBarChart({ operatorStats = [] }) {
  const data = useMemo(() => buildHomeBarData(operatorStats), [operatorStats]);
  const hasData = data.length > 0;

  return (
    <Box sx={{ ...styles.firstCard, ...styles.chartRoot, ...styles.chartText }}>
      <Box sx={styles.headerRow}>
        <Typography variant="subtitle2" sx={styles.headerTitle}>
          Files by Operator
        </Typography>
        <InlineLegend />
      </Box>

      <Box sx={styles.chartContainer}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={rechartsStyles.margin}
              barCategoryGap="18%"
              maxBarSize={72}
            >
              <XAxis dataKey="operatorCode" tick={{ fontSize: 10 }} height={20} interval={0} />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(t) => `${t}%`}
                tick={{ fontSize: 10 }}
                width={30}
              />
              <BarTooltip
                formatter={(v, name, props) => {
                  const display = props?.payload?.__display;
                  const key = name === 'Success' ? 'percentSuccess' : 'percentFailure';
                  const val = display?.[key];
                  return [`${Number.isFinite(val) ? val : Math.round(v)}%`, name];
                }}
                wrapperStyle={rechartsStyles.tooltip}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.operatorName || label}
              />
              <Bar dataKey="percentSuccess" stackId="a" fill={CHART_COLORS[0]} name="Success" />
              <Bar dataKey="percentFailure" stackId="a" fill={CHART_COLORS[1]} name="Failure" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="caption" align="center" sx={{ opacity: 0.8 }}>
            No data available
          </Typography>
        )}
      </Box>
    </Box>
  );
}
